import React from 'react';
import { Button, Empty, Modal, Typography } from 'antd';
import { useSelector } from 'react-redux';
import { CSVLink } from 'react-csv';

import { selectors } from 'state';
import { formatters } from 'services';
import { AreaChart } from 'components';
import AfsEodPriceYieldChart from './AfsEodPriceYieldChart';

type Props = {
  visible: boolean;
  hide: () => void;
};

const AssetIntradayPreviewModal: React.FC<Props> = ({ visible, hide }) => {
  const preview = useSelector(selectors.assetIntradayData);
  if (!preview) return null;
  const supportsYields = preview.supports_yield_preview;
  const csvFields = supportsYields
    ? ['timestamp', 'bid', 'ask', 'close', 'bid_yield', 'ask_yield']
    : ['timestamp', 'bid', 'ask', 'open_price'];

  return (
    <Modal
      open={visible}
      onCancel={hide}
      width={supportsYields ? 900 : undefined}
      title={`${preview.row_limit ? 'Latest stored' : 'Last 24-hour'} intraday prices for: ${preview.ric}`}
      footer={
        preview.row_limit ? (
          <CSVLink
            data={preview.data}
            headers={csvFields.map(field => ({ label: field, key: field }))}
            filename={`${preview.ric}_intraday.csv`}
          >
            <Button type="primary">Download CSV</Button>
          </CSVLink>
        ) : (
          []
        )
      }
    >
      {preview.row_limit ? (
        <Typography.Paragraph type="secondary">
          Showing {preview.data.length.toLocaleString()} stored rows, up to the
          latest {preview.row_limit.toLocaleString()}. Times are UTC.
        </Typography.Paragraph>
      ) : null}
      {!preview.data.length ? (
        <Empty description="No stored intraday prices are available." />
      ) : supportsYields ? (
        <AfsEodPriceYieldChart data={preview.data} intraday />
      ) : (
        <AreaChart
          title=""
          dataSeriesName="bid"
          data={preview.data.map(point => ({
            x: point.timestamp,
            y: point.bid,
          }))}
          yaxisFormatter={formatters.commas4Digits}
          tooltipDateFormat="d MMM HH:mm"
          additionalLines={[
            {
              data: preview.data.map(point => ({
                x: point.timestamp,
                y: point.ask,
              })),
              name: 'ask',
            },
            {
              data: preview.data.map(point => ({
                x: point.timestamp,
                y: point.open_price,
              })),
              name: 'open price',
            },
          ]}
        />
      )}
    </Modal>
  );
};

export default AssetIntradayPreviewModal;
